import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ListingCard } from "@/components/marketplace/ListingCard";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { useListings } from "@/hooks/useListings";
import { Skeleton } from "@/components/ui/skeleton";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import type { Listing } from "@/types";
const heroBgWebpSrcSet =
  "/assets/hero-bg-640.webp 640w, /assets/hero-bg-1024.webp 1024w, /assets/hero-bg-1400.webp 1400w";
const heroBgJpgSrcSet =
  "/assets/hero-bg-640.jpg 640w, /assets/hero-bg-1024.jpg 1024w, /assets/hero-bg-1400.jpg 1400w";
const heroBgFallback = "/assets/hero-bg-1024.jpg";
import {
  Search, Truck, Shield, FlaskConical, BarChart3, FileCheck,
  ArrowRight, MapPin, Package, Users, CheckCircle, Zap, Quote, Star, HelpCircle
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function dbToListing(d: any): Listing {
  return {
    id: d.id,
    title: d.title,
    type: d.type,
    materialType: d.material_type,
    qualityTier: d.quality_tier,
    quantity: Number(d.quantity),
    unit: d.unit,
    pricePerUnit: Number(d.price_per_unit),
    location: { lat: d.lat ?? 40.75, lng: d.lng ?? -74.1, address: d.address, city: d.city, state: d.state, zip: d.zip },
    description: d.description,
    environmentalData: d.environmental_data as any,
    geotechnicalData: d.geotechnical_data as any,
    photos: d.photos ?? [],
    postedBy: d.user_id,
    companyName: "",
    postedDate: d.created_at,
    available: d.available,
  };
}

const stats = [
  { label: "Active Listings", value: 2400, suffix: "+", icon: Package },
  { label: "Verified Haulers", value: 350, suffix: "+", icon: Truck },
  { label: "Tons Moved", value: 1.2, suffix: "M+", decimals: 1, icon: BarChart3 },
  { label: "Registered Users", value: 5800, suffix: "+", icon: Users },
];

const services = [
  {
    icon: Search,
    title: "Materials Marketplace",
    desc: "Browse and list aggregates, soil, sand, mulch, topsoil, and fill materials with full environmental quality data.",
    link: "/marketplace",
  },
  {
    icon: FlaskConical,
    title: "AI Environmental Analysis",
    desc: "Upload ELAP-certified lab data packages. Our AI compares results to NYSDEC Part 375, NJDEP RDCSRS, and DER-10 standards.",
    link: "/analysis",
  },
  {
    icon: Truck,
    title: "Trucking & Logistics",
    desc: "Connect with prequalified haulers licensed for NY & NJ. Coordinate manifesting and compliant transport.",
    link: "/trucking",
  },
  {
    icon: Shield,
    title: "Regulatory Compliance",
    desc: "Automated comparison against NY Part 375 SCOs, NJ Fill Standards, and site-specific criteria for lawful reuse.",
    link: "/analysis",
  },
  {
    icon: MapPin,
    title: "Site Matching",
    desc: "Match source materials to receiving sites based on environmental quality, gradation, quantity, and location.",
    link: "/marketplace",
  },
  {
    icon: FileCheck,
    title: "Transport Manifesting",
    desc: "Generate compliant transport manifests for soil and fill movement across NY & NJ jurisdictions.",
    link: "/trucking",
  },
];

const trustPoints = [
  "NYSDEC Part 375 compliant",
  "NJDEP RDCSRS verified",
  "ELAP-certified lab data",
  "Real-time quality tracking",
];

const Index = () => {
  const navigate = useNavigate();
  const [heroSearch, setHeroSearch] = useState("");
  const { data: dbListings, isLoading } = useListings({
    search: "",
    materialFilter: "all",
    qualityFilter: "all",
    stateFilter: "all",
    typeFilter: "all",
  });

  const featured = (dbListings ?? []).slice(0, 6).map(dbToListing);

  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0, 0.4]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden min-h-[85vh] flex items-center">
        {/* Background image with parallax */}
        <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
          <picture>
            <source
              type="image/webp"
              srcSet={heroBgWebpSrcSet}
              sizes="100vw"
            />
            <source
              type="image/jpeg"
              srcSet={heroBgJpgSrcSet}
              sizes="100vw"
            />
            <img
              src={heroBgFallback}
              alt=""
              width={1400}
              height={788}
              className="h-full w-full object-cover"
              loading="eager"
              decoding="async"
              // @ts-ignore - fetchpriority is valid HTML attr
              fetchpriority="high"
            />
          </picture>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/85 to-primary/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-primary/30" />
        <motion.div className="absolute inset-0 bg-primary" style={{ opacity: overlayOpacity }} />

        <motion.div className="container relative z-10 py-20 lg:py-28" style={{ y: contentY, opacity: contentOpacity }}>
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge className="mb-6 bg-accent/15 text-accent border-accent/30 hover:bg-accent/20 px-4 py-1.5 text-sm font-medium">
                <Zap className="mr-1.5 h-3.5 w-3.5" />
                NY & NJ's #1 Materials Platform
              </Badge>

              <h1 className="font-display text-4xl font-bold tracking-tight text-primary-foreground sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.1]">
                Move Earth.{" "}
                <span className="relative">
                  <span className="text-accent">Stay Compliant.</span>
                  <motion.span
                    className="absolute -bottom-2 left-0 h-1 bg-accent rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                  />
                </span>
              </h1>

              <p className="mt-6 text-base sm:text-lg lg:text-xl text-primary-foreground/70 max-w-full sm:max-w-lg leading-relaxed">
                The marketplace where contractors, consultants, and haulers trade aggregates, soil, and fill with
                AI-powered environmental compliance built in.
              </p>

              {/* Hero search bar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="mt-8"
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    navigate(`/marketplace?search=${encodeURIComponent(heroSearch)}`);
                  }}
                  className="flex w-full max-w-full sm:max-w-lg rounded-xl bg-primary-foreground/10 backdrop-blur-md border border-primary-foreground/15 overflow-hidden focus-within:border-accent/50 transition-colors"
                >
                  <div className="flex items-center pl-4 text-primary-foreground/40">
                    <Search className="h-5 w-5" />
                  </div>
                  <input
                    type="text"
                    value={heroSearch}
                    onChange={(e) => setHeroSearch(e.target.value)}
                    placeholder="Search clean fill, topsoil, gravel..."
                    className="flex-1 bg-transparent px-3 py-3.5 text-primary-foreground placeholder:text-primary-foreground/40 outline-none text-base"
                  />
                  <Button
                    type="submit"
                    className="m-1.5 bg-accent text-accent-foreground hover:bg-orange-light rounded-lg px-4 sm:px-6 shrink-0"
                  >
                    Search
                  </Button>
                </form>
              </motion.div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Button size="lg" className="bg-accent text-accent-foreground hover:bg-orange-light text-base px-8 h-13 shadow-lg shadow-accent/25" asChild>
                  <Link to="/marketplace">
                    <Search className="mr-2 h-5 w-5" />
                    Browse Materials
                  </Link>
                </Button>
                <Button size="lg" className="border border-primary-foreground/25 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 text-base px-8 h-13 backdrop-blur-sm" asChild>
                  <Link to="/post-listing">
                    <Package className="mr-2 h-5 w-5" />
                    List Your Materials
                  </Link>
                </Button>
              </div>

              {/* Trust signals */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {trustPoints.map((point, i) => (
                  <motion.div
                    key={point}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 + i * 0.15 }}
                    className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/60"
                  >
                    <CheckCircle className="h-4 w-4 text-quality-unrestricted shrink-0" />
                    <span>{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right — floating stat cards */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block relative"
            >
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, i) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 + i * 0.15, duration: 0.4 }}
                  >
                    <Card className="bg-card/10 backdrop-blur-md border-primary-foreground/10 hover:bg-card/15 transition-colors">
                      <CardContent className="p-5 text-center">
                        <stat.icon className="h-8 w-8 text-accent mx-auto mb-2" />
                        <p className="font-display text-3xl font-bold text-primary-foreground">
                          <AnimatedCounter value={stat.value} suffix={stat.suffix} decimals={stat.decimals} />
                        </p>
                        <p className="text-xs text-primary-foreground/50 mt-1">{stat.label}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Mobile stats bar */}
      <section className="border-b bg-card lg:hidden">
        <div className="container grid grid-cols-2 gap-4 py-6">
          {stats.map((stat) => (
            <div key={stat.label} className="flex items-center gap-3 justify-center">
              <stat.icon className="h-7 w-7 text-accent" />
              <div>
                <p className="font-display text-xl font-bold">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} decimals={stat.decimals} />
                </p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications & Partners */}
      <section className="border-b bg-card py-10">
        <div className="container">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-xs font-medium uppercase tracking-widest text-muted-foreground mb-6"
          >
            Regulatory Standards &amp; Certifications We Support
          </motion.p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {[
              { name: "NYSDEC", full: "NY Dept. of Environmental Conservation", icon: Shield },
              { name: "NJDEP", full: "NJ Dept. of Environmental Protection", icon: Shield },
              { name: "ELAP", full: "Environmental Laboratory Accreditation", icon: FlaskConical },
              { name: "DER-10", full: "Technical Guidance for Site Investigation", icon: FileCheck },
              { name: "Part 375", full: "Soil Cleanup Objectives", icon: BarChart3 },
              { name: "RDCSRS", full: "Remediation Standards", icon: CheckCircle },
            ].map((cert, i) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
                className="flex items-center gap-2.5 group"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 group-hover:bg-accent/20 transition-colors">
                  <cert.icon className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="font-display text-sm font-semibold">{cert.name}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">{cert.full}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Analysis Promo Banner */}
      <section className="relative overflow-hidden bg-primary py-14 lg:py-16">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-accent blur-3xl" />
          <div className="absolute -left-10 bottom-0 h-60 w-60 rounded-full bg-accent blur-3xl" />
        </div>
        <div className="container relative z-10">
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:justify-between">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="max-w-xl text-center lg:text-left"
            >
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5">
                <Zap className="h-4 w-4 text-accent" />
                <span className="text-xs font-semibold uppercase tracking-wider text-accent">AI-Powered</span>
              </div>
              <h2 className="font-display text-3xl font-bold text-primary-foreground lg:text-4xl">
                Instant Environmental <br className="hidden sm:block" />
                <span className="text-accent">Compliance Analysis</span>
              </h2>
              <p className="mt-4 text-base text-primary-foreground/70 leading-relaxed">
                Upload your lab data and get instant comparisons against NYSDEC Part 375 and NJDEP RDCSRS standards. 
                Know your material's quality tier in seconds — not days.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
                <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" asChild>
                  <Link to="/analysis">
                    Try AI Analysis <FlaskConical className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10" asChild>
                  <Link to="/register">
                    Sign Up Free <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="grid grid-cols-2 gap-3 sm:gap-4"
            >
              {[
                { label: "Standards Checked", value: "6+", desc: "NY & NJ regs" },
                { label: "Parameters Parsed", value: "50+", desc: "Metals, VOCs, SVOCs" },
                { label: "Analysis Time", value: "<30s", desc: "Per report" },
                { label: "Accuracy Rate", value: "99%", desc: "Regulatory match" },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 + i * 0.08 }}
                  viewport={{ once: true }}
                  className="rounded-xl border border-primary-foreground/10 bg-primary-foreground/5 backdrop-blur-sm p-4 text-center"
                >
                  <p className="font-display text-2xl font-bold text-accent">{stat.value}</p>
                  <p className="text-xs font-medium text-primary-foreground/90 mt-1">{stat.label}</p>
                  <p className="text-[10px] text-primary-foreground/50 mt-0.5">{stat.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="container">
          <div className="flex items-end justify-between mb-10">
            <div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-display text-3xl font-bold"
              >
                Featured <span className="text-accent">Listings</span>
              </motion.h2>
              <p className="mt-2 text-muted-foreground">Recently posted materials available across NY & NJ</p>
            </div>
            <Button variant="outline" className="hidden sm:flex" asChild>
              <Link to="/marketplace">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {isLoading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-80 rounded-lg" />
              ))}
            </div>
          ) : featured.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((listing, i) => (
                <motion.div
                  key={listing.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  viewport={{ once: true }}
                >
                  <ListingCard listing={listing} />
                </motion.div>
              ))}
            </div>
          ) : null}

          <div className="mt-8 text-center sm:hidden">
            <Button variant="outline" asChild>
              <Link to="/marketplace">
                View All Listings <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 lg:py-20 bg-secondary">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold">
              Everything You Need for <span className="text-accent">Compliant Material Movement</span>
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              From environmental analysis to final delivery — one platform for source sites, receiving sites, consultants, and haulers.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Link to={service.link}>
                  <Card className="h-full transition-all hover:shadow-lg hover:border-accent/40 group cursor-pointer">
                    <CardContent className="p-6">
                      <service.icon className="h-10 w-10 text-accent mb-4 group-hover:scale-110 transition-transform" />
                      <h3 className="font-display text-lg font-semibold mb-2">{service.title}</h3>
                      <p className="text-sm text-muted-foreground">{service.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-24 overflow-hidden">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/20 px-3 py-1 text-xs font-medium uppercase tracking-wider">
              Simple 4-Step Process
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl font-bold">
              From Listing to <span className="text-accent">Delivery</span>
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              Our streamlined workflow gets materials moving — compliantly — in four easy steps.
            </p>
          </motion.div>

          <div className="relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-16 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-accent/20 via-accent/40 to-accent/20" />

            <div className="grid gap-10 sm:grid-cols-2 md:grid-cols-4">
              {[
                {
                  step: "01",
                  icon: Package,
                  title: "List Your Materials",
                  desc: "Post available soil, fill, or aggregates with environmental quality data, quantity, and location details.",
                  color: "from-accent/20 to-accent/5",
                },
                {
                  step: "02",
                  icon: FlaskConical,
                  title: "AI Compliance Check",
                  desc: "Upload ELAP-certified lab data. Our AI cross-references NYSDEC Part 375, NJDEP RDCSRS, and DER-10 standards.",
                  color: "from-quality-unrestricted/20 to-quality-unrestricted/5",
                },
                {
                  step: "03",
                  icon: MapPin,
                  title: "Match & Connect",
                  desc: "Get matched with compatible source or receiving sites based on quality tier, gradation, and proximity.",
                  color: "from-blue-500/20 to-blue-500/5",
                },
                {
                  step: "04",
                  icon: Truck,
                  title: "Haul & Deliver",
                  desc: "Coordinate with verified haulers, generate compliant manifests, and track materials from source to site.",
                  color: "from-accent/20 to-accent/5",
                },
              ].map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.12, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="relative text-center group"
                >
                  {/* Step circle */}
                  <div className="relative mx-auto mb-5">
                    <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${item.color} border border-border shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-300`}>
                      <item.icon className="h-7 w-7 text-accent" />
                    </div>
                    <span className="absolute -top-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-accent-foreground font-display text-xs font-bold shadow-md">
                      {item.step}
                    </span>
                  </div>

                  {/* Arrow between steps (mobile/tablet: hidden) */}
                  {i < 3 && (
                    <div className="hidden md:flex absolute top-8 -right-5 text-accent/30">
                      <ArrowRight className="h-5 w-5" />
                    </div>
                  )}

                  <h3 className="font-display text-base sm:text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA below steps */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Button size="lg" className="bg-accent text-accent-foreground hover:bg-orange-light shadow-lg shadow-accent/20" asChild>
              <Link to="/marketplace">
                Get Started Now <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-20 bg-secondary">
        <div className="container">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-3xl font-bold"
            >
              Trusted by <span className="text-accent">Industry Professionals</span>
            </motion.h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Hear from contractors, consultants, and haulers who rely on SoilConnect Pro every day.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                quote: "We used to spend days searching for clean fill. Now I find verified materials in minutes, with lab data I can actually trust. It's transformed how we bid on projects.",
                name: "Mike Caruso",
                title: "Project Manager",
                company: "Caruso Excavation LLC",
                role: "Contractor",
                stars: 5,
              },
              {
                quote: "The AI compliance analysis saves me hours per report. It cross-references Part 375 and RDCSRS instantly — catches things I might miss after a long day reviewing spreadsheets.",
                name: "Dr. Sarah Patel",
                title: "Senior Environmental Scientist",
                company: "GreenShield Consulting",
                role: "Consultant",
                stars: 5,
              },
              {
                quote: "Finding backhaul loads was always hit-or-miss. SoilConnect Pro connects me with jobs right along my routes. My trucks are running fuller and my margins are up 20%.",
                name: "Tony Rizzo",
                title: "Fleet Operations Manager",
                company: "Tri-State Hauling Inc.",
                role: "Hauler",
                stars: 5,
              },
            ].map((testimonial, i) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-border/60 hover:border-accent/30 transition-colors">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex gap-0.5 mb-4">
                      {Array.from({ length: testimonial.stars }).map((_, s) => (
                        <Star key={s} className="h-4 w-4 fill-accent text-accent" />
                      ))}
                    </div>
                    <Quote className="h-8 w-8 text-accent/20 mb-3 -scale-x-100" />
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                      "{testimonial.quote}"
                    </p>
                    <div className="mt-6 pt-4 border-t border-border/60">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent font-display font-bold text-sm">
                          {testimonial.name.split(" ").map((n) => n[0]).join("")}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{testimonial.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {testimonial.title} · {testimonial.company}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="mt-3 text-xs">
                        {testimonial.role}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-secondary/30">
        <div className="container max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
              <HelpCircle className="h-6 w-6 text-accent" />
            </div>
            <h2 className="font-display text-3xl font-bold">
              Frequently Asked <span className="text-accent">Questions</span>
            </h2>
            <p className="mt-2 text-muted-foreground">Everything you need to know about SoilConnect Pro</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <Accordion type="single" collapsible className="space-y-3">
              {[
                {
                  q: "What is SoilConnect Pro?",
                  a: "SoilConnect Pro is a marketplace connecting contractors, environmental consultants, and haulers across New York and New Jersey. We make it easy to buy, sell, and transport fill materials while staying compliant with NYSDEC Part 375 and NJDEP RDCSRS standards."
                },
                {
                  q: "How does the AI environmental analysis work?",
                  a: "Upload your lab data — whether it's a PDF report, CSV, or raw text — and our AI instantly compares every parameter against NYSDEC Part 375 SCOs and NJDEP RDCSRS standards. You'll get a quality tier classification, exceedance flags, and handling recommendations in under 30 seconds."
                },
                {
                  q: "Is it free to list materials?",
                  a: "Yes! Creating an account and posting listings is completely free. There are no listing fees, commissions, or hidden charges. We believe in making compliant material reuse accessible to everyone in the industry."
                },
                {
                  q: "What types of materials can I list?",
                  a: "You can list clean fill, topsoil, sand, gravel, crushed stone, recycled concrete, aggregates, mulch, general fill, and contaminated soil. Each listing includes quality tier classification so buyers know exactly what they're getting."
                },
                {
                  q: "How do quality tiers work?",
                  a: "Materials are classified into five tiers based on environmental testing: Unrestricted (cleanest), Residential, Commercial, Industrial, and Restricted. These tiers align with NYSDEC Part 375 Soil Cleanup Objectives and determine where material can be legally reused."
                },
                {
                  q: "Can I communicate with buyers/sellers directly?",
                  a: "Absolutely. Our built-in messaging system lets you contact sellers directly from any listing. Discuss pricing, logistics, lab data, and schedule deliveries — all within the platform."
                },
                {
                  q: "What areas do you serve?",
                  a: "We currently focus on New York and New Jersey, covering all five NYC boroughs, Long Island, Westchester, and Northern/Central New Jersey. We're expanding to additional states soon."
                },
                {
                  q: "Do I need ELAP-certified lab results?",
                  a: "For our AI analysis to provide regulatory-grade classifications, we recommend using ELAP-certified laboratory data. However, you can list materials with any available testing documentation — the quality tier can be set manually as well."
                },
              ].map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="rounded-lg border bg-card px-5 data-[state=open]:shadow-sm"
                >
                  <AccordionTrigger className="text-left font-medium hover:no-underline py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden py-24 lg:py-32 bg-primary">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--accent)/0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,hsl(var(--accent)/0.1),transparent_50%)]" />
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.08, 0.15, 0.08] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-accent blur-3xl"
          />
          <motion.div
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.05, 0.12, 0.05] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-accent blur-3xl"
          />
        </div>

        <div className="container relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-5 py-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">Join 5,800+ Professionals</span>
            </div>

            <h2 className="font-display text-4xl font-bold text-primary-foreground lg:text-5xl xl:text-6xl">
              Stop Searching. <br className="hidden sm:block" />
              <span className="text-accent">Start Moving Material.</span>
            </h2>

            <p className="mx-auto mt-6 max-w-2xl text-lg text-primary-foreground/60 leading-relaxed">
              The only marketplace built for NY &amp; NJ environmental compliance. 
              List your material, verify quality with AI, and connect with verified haulers — all in one platform.
            </p>

            <div className="mx-auto mt-8 flex max-w-md flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button size="lg" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg shadow-accent/25 font-semibold text-base sm:w-auto px-8" asChild>
                <Link to="/register">
                  Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="w-full border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10 sm:w-auto px-8" asChild>
                <Link to="/marketplace">Browse Marketplace</Link>
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-primary-foreground/50">
              {["No credit card required", "NYSDEC & NJDEP compliant", "AI-powered analysis"].map((item) => (
                <span key={item} className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-accent/70" />
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
